import React from 'react'
import { SimpleLayout, SimpleMessage } from '../../layouts/simple'
import { Teacher } from '../../providers/context'
import { SpecialPanel } from '../../layouts/components/panel'
import { linkTo, pluralize } from '../../utils'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import { DateInput, NumberInput, TextInput } from '../../components/form/textInput'
import { Select } from '../../components/form/select'
import { Tooltip } from '../../components/simple/tooltip'
import { Popups } from '../../providers/popups'
import { LabeledCheckbox } from '../../components/form/checkbox'
import { Divider } from '../../components/simple/divider'
import { ControlledModal } from '../../layouts/components/modal'
import { IconTimes } from '../../icons/library'
import { ConferenceTimeRange } from './components/conferenceComponents'
import { TimePicker } from '../../components/form/timePicker'
import { format } from 'date-fns-tz'
import { isPast, parseISO } from 'date-fns'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { useNavigate } from 'react-router-dom'
import { useLanguages } from '../../providers/languages'

const lengthOptions = [
  {
    value: 10,
    name: '10 Minutes',
  },
  {
    value: 15,
    name: '15 Minutes',
  },
  {
    value: 20,
    name: '20 Minutes',
  },
  {
    value: 30,
    name: '30 Minutes',
  },
  {
    value: 45,
    name: '45 Minutes',
  },
  {
    value: 60,
    name: '60 Minutes',
  },
  {
    value: 'custom',
    name: 'Custom',
  },
]

const locationOptions = [
  {
    value: 'inperson',
    name: 'In-Person',
  },
  {
    value: 'virtual',
    name: 'Virtual',
  },
  {
    value: 'both',
    name: "Family's Choice",
  },
]

const inviteOptions = [
  {
    value: 'teachers',
    name: 'Teachers send invites',
  },
  {
    value: 'admin',
    name: 'Admin sends invites',
  },
]

export const PTCNewConferencePage: React.FC<{ user: Teacher }> = ({ user }) => {
  const [loading, setLoading] = React.useState(false)

  const [name, setName] = React.useState('')
  const schools = (user.districtAccess ? user.district : user).schools
  const schoolOptions = React.useMemo(
    () => [
      {
        value: '',
        name: 'Select School',
        initialOnly: true,
      },
      ...schools.filter((s) => s.ptcEnabled).map((s) => ({ value: s.id, name: s.name })),
    ],
    [user]
  )

  if (schoolOptions.length === 1) {
    return <SimpleMessage>No schools available for conferences</SimpleMessage>
  }

  const [schoolOption, setSchool] = React.useState(schoolOptions.length != 2 ? schoolOptions[0] : schoolOptions[1])
  const school = schoolOption.value === '' ? undefined : schools.find((s) => s.id === schoolOption.value)

  const [selectedLength, setLength] = React.useState(lengthOptions[0])
  const [customLength, setCustomLength] = React.useState<number | undefined>(undefined)
  const [bufferTime, setBufferTime] = React.useState<number | undefined>(undefined)
  const length = selectedLength.value === 'custom' ? customLength : +selectedLength.value

  const [location, setLocation] = React.useState(locationOptions[0])

  const [whoInvites, setWhoInvites] = React.useState(inviteOptions[0])

  const multilingualOptions = [
    {
      value: 0,
      name: 'No',
    },
    {
      value: 1,
      name: 'Lexikeet',
    },
    {
      value: 2,
      name: 'District Interpreter',
    },
  ].filter((o) => o.value !== 1 || school?.lexikeet)
  const [multilingual, setMultilingual] = React.useState(multilingualOptions[0])
  React.useEffect(() => {
    if (!school?.lexikeet && multilingual.value === 1) setMultilingual(multilingualOptions[0])
  }, [school, multilingual])

  const [teachersViewAll, setTeachersViewAll] = React.useState(false)

  const [editingInterpreters, setEditingInterpreters] = React.useState(false)

  const [selectedInterpreters, setSelectedInterpreters] = React.useState<Record<string, number>>({})
  const interpreterCount = Object.values(selectedInterpreters).reduce((a, b) => a + b, 0)

  const [conferenceTimeRanges, setConferenceTimeRanges] = React.useState<ConferenceTimeRange[]>([])

  const valid = name && schoolOption.value && length && conferenceTimeRanges.length > 0

  // Validation messages
  const validationMessages = []
  if (!name) validationMessages.push('Conference name is required')
  if (!schoolOption.value) validationMessages.push('School selection is required')
  if (!length) validationMessages.push('Conference length is required')
  if (conferenceTimeRanges.length === 0) validationMessages.push('At least one conference time is required')

  const navigate = useNavigate()
  const submitConference = () => {
    if (!valid) return
    setLoading(true)
    makeRequest<{ id?: number; error?: string }>('/frontend_api/newConference', {
      conferenceDate: conferenceTimeRanges.map((r) => r.date),
      startTime: conferenceTimeRanges.map((r) => r.startTime),
      endTime: conferenceTimeRanges.map((r) => r.endTime),
      conferenceSchool: schoolOption.value,
      interpretersData:
        multilingual.value === 2
          ? JSON.stringify(Object.entries(selectedInterpreters).map(([k, v]) => ({ language: k, availability: v })))
          : undefined,
      conferenceName: name,
      conferenceLocation: location.value,
      multilingual: multilingual.value,
      conferenceDuration: length,
      conferenceBufferTime: bufferTime ?? 0,
      conferenceInviter: whoInvites.value,
      allowTeacherViewAll: teachersViewAll,
    })
      .then((response) => {
        if (response.error) Popups.simplePopup(response.error)
        else
          navigate(
            whoInvites.value === 'admin'
              ? `/ptc/adminConferenceVerification/exclusionClasses/${response.id}`
              : `/ptc/conference/${response.id}`
          )
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoading(false))
  }

  return (
    <SimpleLayout>
      <SpecialPanel className={`mb-8 mt-8 sm:!w-auto ${loading ? 'pointer-events-none opacity-50' : ''}`}>
        <div className="flex w-full items-center justify-between">
          <div className="text-2xl font-bold">Conference Registration</div>
          <Button
            small
            buttonStyle={ButtonStyle.link}
            onClick={linkTo(
              'https://reachmyteach.zendesk.com/hc/en-us/articles/38105263126169-Creating-a-Conference',
              true
            )}
          >
            View Guide
          </Button>
        </div>
        <div className="mt-2 flex gap-2 self-stretch">
          <div className="flex flex-col gap-2 border-r border-gray-100 pr-2">
            <div>Conference Name</div>
            <TextInput className="w-full" blue value={name} onChange={setName} />
            {schoolOptions.length > 2 && (
              <>
                <div>School</div>
                <Select blue options={schoolOptions} value={schoolOption} onChange={setSchool} />
              </>
            )}
            <div>Conference Length</div>
            <Select blue options={lengthOptions} value={selectedLength} onChange={setLength} />
            {selectedLength.value === 'custom' && (
              <NumberInput
                blue
                placeholder="Enter custom length (minutes)"
                className="w-full"
                value={customLength}
                onChange={setCustomLength}
              />
            )}
            <div>Time Between Appointments (minutes)</div>
            <NumberInput
              blue
              className="w-full"
              value={bufferTime}
              onChange={setBufferTime}
              max={length}
              placeholder="0"
            />
            <div>Type</div>
            <Select blue options={locationOptions} value={location} onChange={setLocation} />
            <Divider />
            <Tooltip text='"Teachers send invites" allows any teacher to invite their families. "Admin send invites" requires that admin send the first invites, and teachers can send invites later if needed.'>
              <div>Who invites families?</div>
            </Tooltip>
            <Select blue options={inviteOptions} value={whoInvites} onChange={setWhoInvites} />
            <LabeledCheckbox
              label="Teachers view all appointments"
              tooltip="Should teachers be able to view the entire conference schedule, or only their appointments."
              checked={teachersViewAll}
              onChecked={setTeachersViewAll}
            />
            <Divider />
            <div>Multilingual</div>
            <Select
              blue
              options={multilingualOptions}
              value={multilingual}
              onChange={async (o) => {
                if (
                  o.value === 1 &&
                  !(await Popups.simpleConfirm(
                    <b>Lexikeet charges a 30-minute minimum for all conferences</b>,
                    'Please confirm you are aware and accept these costs before proceeding.'
                  ))
                )
                  return
                setMultilingual(o)
              }}
            />
            {multilingual.value === 2 && (
              <>
                <Button buttonStyle={ButtonStyle.warning} onClick={() => setEditingInterpreters(true)}>
                  {interpreterCount} {pluralize(interpreterCount, 'interpreter')} <b>(Edit)</b>
                </Button>
                <ConferenceInterpretersModal
                  open={editingInterpreters}
                  onClose={() => setEditingInterpreters(false)}
                  interpreters={selectedInterpreters}
                  setInterpreters={setSelectedInterpreters}
                />
              </>
            )}
          </div>
          <ConferenceTimeEditor ranges={conferenceTimeRanges} setRanges={setConferenceTimeRanges} />
        </div>
        {!valid && validationMessages.length > 0 && (
          <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-700">
            <div className="font-medium">Please complete the following to submit your conference:</div>
            <ul className="mt-1 list-inside list-disc">
              {validationMessages.map((message, index) => (
                <li key={index}>{message}</li>
              ))}
            </ul>
          </div>
        )}
        <Button logo onClick={submitConference} disabled={!valid}>
          Submit
        </Button>
      </SpecialPanel>
    </SimpleLayout>
  )
}

export const ConferenceTimeEditor: React.FC<{
  ranges: ConferenceTimeRange[]
  setRanges: React.Dispatch<React.SetStateAction<ConferenceTimeRange[]>>
}> = ({ ranges, setRanges }) => {
  const [currentDate, setCurrentDate] = React.useState<Date | undefined>(undefined)
  const [currentStartTime, setCurrentStartTime] = React.useState('17:00')
  const [currentEndTime, setCurrentEndTime] = React.useState('20:00')

  // Helper function to convert 24-hour time to 12-hour format
  const formatTime12Hour = (time24: string) => {
    const [hours, minutes] = time24.split(':').map(Number)
    const period = hours >= 12 ? 'PM' : 'AM'
    const hours12 = hours % 12 || 12
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`
  }

  const isOverlapping = (newDate: Date, newStartTime: string, newEndTime: string) => {
    const newStart = new Date(`${format(newDate, 'yyyy-MM-dd')}T${newStartTime}`)
    const newEnd = new Date(`${format(newDate, 'yyyy-MM-dd')}T${newEndTime}`)
    return ranges.some((r) => {
      const existingStart = new Date(`${r.date}T${r.startTime}`)
      const existingEnd = new Date(`${r.date}T${r.endTime}`)
      return (
        newDate.getTime() === parseISO(r.date).getTime() &&
        ((newStart >= existingStart && newStart < existingEnd) || // New start is within existing range
          (newEnd > existingStart && newEnd <= existingEnd) || // New end is within existing range
          (newStart <= existingStart && newEnd >= existingEnd)) // New range fully contains existing range
      )
    })
  }

  const isAddButtonDisabled =
    !currentDate ||
    !currentStartTime ||
    !currentEndTime ||
    isPast(currentDate) ||
    isOverlapping(currentDate, currentStartTime, currentEndTime) ||
    new Date(`${format(currentDate!, 'yyyy-MM-dd')}T${currentStartTime}`) >=
      new Date(`${format(currentDate!, 'yyyy-MM-dd')}T${currentEndTime}`)

  const sortedRanges = [...ranges].sort((a, b) => {
    return new Date(`${a.date}T${a.startTime}`).getTime() - new Date(`${b.date}T${b.startTime}`).getTime()
  })

  return (
    <div className="flex flex-grow flex-col gap-2">
      <div className="self-stretch text-center text-xl font-bold">Conference Times</div>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <div className="mx-4">Conference Date</div>
          <div className="mx-6">Start Time</div>
          <div className="mx-7">End Time</div>
        </div>
        {sortedRanges.map((r) => (
          <div className="flex items-center gap-2" key={r.startTime + r.endTime + r.date}>
            <DateInput blue disabled value={parseISO(r.date)} />
            <TimePicker blue disabled time={r.startTime} />
            <TimePicker blue disabled time={r.endTime} />
            <Button
              disabled={!r.editable}
              tooltip={r.editable ? undefined : 'This time cannot be removed since it has appointments'}
              shape={ButtonShape.iconSmall}
              onClick={() => setRanges((rs) => rs.filter((rr) => rr !== r))}
            >
              <IconTimes />
            </Button>
          </div>
        ))}
        {ranges.length === 0 && (
          <div className="rounded bg-warning px-2 py-1 text-center">No conference times added</div>
        )}
        <div className="flex gap-2">
          <DateInput blue value={currentDate} onChange={setCurrentDate} />
          <TimePicker blue time={currentStartTime} setTime={setCurrentStartTime} />
          <Tooltip text="This is the end time of the last conference in this time block.">
            <TimePicker blue time={currentEndTime} setTime={setCurrentEndTime} />
          </Tooltip>
          <Button
            buttonStyle={ButtonStyle.blue}
            disabled={isAddButtonDisabled}
            onClick={() => {
              setRanges((rs) => [
                ...rs,
                {
                  date: format(currentDate!, 'yyyy-MM-dd'),
                  startTime: currentStartTime,
                  endTime: currentEndTime,
                  editable: true,
                },
              ])
              // Reset form after adding
              setCurrentDate(undefined)
              setCurrentStartTime('17:00')
              setCurrentEndTime('20:00')
            }}
          >
            + Add This Time
          </Button>
        </div>
        {currentDate && currentStartTime && currentEndTime && !isAddButtonDisabled && (
          <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <strong>Ready to add:</strong> {format(currentDate, 'MMM d, yyyy')} from{' '}
            {formatTime12Hour(currentStartTime)} to {formatTime12Hour(currentEndTime)}
            <br />
            <span className="text-xs">Click "Add This Time" to confirm</span>
          </div>
        )}
        {isAddButtonDisabled && currentDate && (
          <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-700">
            {isPast(currentDate) && '⚠️ Cannot add past dates'}
            {!isPast(currentDate) &&
              isOverlapping(currentDate, currentStartTime, currentEndTime) &&
              '⚠️ Time conflicts with existing appointment'}
            {!isPast(currentDate) &&
              !isOverlapping(currentDate, currentStartTime, currentEndTime) &&
              new Date(`${format(currentDate!, 'yyyy-MM-dd')}T${currentStartTime}`) >=
                new Date(`${format(currentDate!, 'yyyy-MM-dd')}T${currentEndTime}`) &&
              '⚠️ Start time must be before end time'}
          </div>
        )}
      </div>
    </div>
  )
}

export const ConferenceInterpretersModal: React.FC<{
  open: boolean
  onClose: () => void
  interpreters: Record<string, number>
  setInterpreters: React.Dispatch<React.SetStateAction<Record<string, number>>>
}> = ({ open, onClose, interpreters, setInterpreters }) => {
  const languages = useLanguages()
  const languageOptions = React.useMemo(
    () => [
      {
        name: 'Select Language',
        value: '',
        initialOnly: true,
      },
      ...languages.getOptions((l) => l.lexikeetCode !== undefined),
    ],
    [languages]
  )
  const [interpreterLanguage, setinterpreterLanguage] = React.useState(languageOptions[0])
  const [interpreterNumber, setinterpreterNumber] = React.useState<number | undefined>(undefined)

  return (
    <ControlledModal
      open={open}
      onOpenChange={(o) => !o && onClose}
      title="District Interpreters Information"
      size="sm"
    >
      Please select the language of your staffed interpreters and indicated how many of that language will be available.
      Your interpreters will need to be notified as ReachMyTeach does not have their information.
      <div className="flex gap-2">
        <div className="flex flex-col gap-2">
          Language
          <Select
            blue
            options={languageOptions.filter((l) => !(l.value in interpreters))}
            value={interpreterLanguage}
            onChange={setinterpreterLanguage}
          />
          Number Available
          <NumberInput blue value={interpreterNumber} onChange={setinterpreterNumber} />
          <Button
            buttonStyle={ButtonStyle.warning}
            onClick={() => {
              setinterpreterLanguage(languageOptions[0])
              setinterpreterNumber(undefined)
              setInterpreters((si) => ({
                ...si,
                [interpreterLanguage.value]: interpreterNumber!,
              }))
            }}
            disabled={!interpreterLanguage.value || !interpreterNumber}
          >
            Add Interpreter
          </Button>
        </div>
        <div className="flex flex-grow flex-col gap-1">
          {Object.entries(interpreters).map(([lang, number]) => (
            <div className="flex items-center justify-between gap-1 rounded bg-gray-50 px-2 py-1 shadow" key={lang}>
              <div>
                {languages.find(lang)?.name}: {number}
              </div>
              <Button
                shape={ButtonShape.iconSmall}
                onClick={() =>
                  setInterpreters((si) => {
                    const { [lang]: _, ...rest } = si
                    return rest
                  })
                }
              >
                <IconTimes />
              </Button>
            </div>
          ))}
        </div>
      </div>
      <Divider className="mt-2" />
      <Button className="self-center" logo onClick={onClose}>
        Done
      </Button>
    </ControlledModal>
  )
}
